import { css } from 'styled-components';
import { Rule, StyleProps } from './variants';

/* How a rail handles the card that's inevitably mid-cut at its left/right
   boundary — orthogonal to the card's own visual style (see variants.ts). */
export type EdgeVariant = {
    id: number;
    label: string;
    /* One line shown under the caption in the lab, explaining the mechanism. */
    note: string;
    fadeEdges?: boolean;
    /* Target number of fully visible cards on a desktop-width rail; the card
       width is computed from the rail's own size so that many always fit
       exactly, with nothing left mid-card at either edge. */
    sizeToFit?: number;
    rail?: Rule;
    card?: Rule;
};

const overlayEdges = (content: Rule) => css<StyleProps>`
    &::before,
    &::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        z-index: 2;
        pointer-events: none;
    }
    &::before {
        left: 0;
    }
    &::after {
        right: 0;
    }
    ${content}
`;

export const EDGE_VARIANTS: EdgeVariant[] = [
    {
        id: 1,
        label: '01 — soft fade',
        note: 'A transparency mask dissolves the cut card into the background — the default treatment so far.',
        fadeEdges: true,
    },
    {
        id: 2,
        label: '02 — scrim gradient',
        note: 'An opaque colour gradient rather than a transparency mask — reads more solid, and works even where mask-image support is patchy.',
        rail: overlayEdges(css<StyleProps>`
            &::before,
            &::after {
                width: 3.5rem;
            }
            &::before {
                background: linear-gradient(to right, ${({ theme }) => theme.colour.DARK_GREY}, transparent);
            }
            &::after {
                background: linear-gradient(to left, ${({ theme }) => theme.colour.DARK_GREY}, transparent);
            }
        `),
    },
    {
        id: 3,
        label: '03 — blur edge',
        note: 'The edge softens into a blur instead of dissolving to nothing, keeping a hint of colour and shape.',
        rail: overlayEdges(css<StyleProps>`
            &::before,
            &::after {
                width: 3rem;
                backdrop-filter: blur(6px);
                -webkit-backdrop-filter: blur(6px);
            }
            &::before {
                mask-image: linear-gradient(to right, black, transparent);
                -webkit-mask-image: linear-gradient(to right, black, transparent);
            }
            &::after {
                mask-image: linear-gradient(to left, black, transparent);
                -webkit-mask-image: linear-gradient(to left, black, transparent);
            }
        `),
    },
    {
        id: 4,
        label: '04 — vignette shadow',
        note: 'A shadow-based vignette darkens the edges rather than fading them to transparent.',
        rail: css<StyleProps>`
            &::after {
                content: '';
                position: absolute;
                inset: 0;
                z-index: 2;
                pointer-events: none;
                box-shadow:
                    inset 2.5rem 0 1.5rem -1.5rem rgba(0, 0, 0, 0.7),
                    inset -2.5rem 0 1.5rem -1.5rem rgba(0, 0, 0, 0.7);
            }
        `,
    },
    {
        id: 5,
        label: '05 — scroll-snap',
        note: 'Cards always settle fully in view once you stop dragging — no mid-card rest position after interaction (auto-scroll still passes through cards continuously).',
        rail: css<StyleProps>`
            scroll-snap-type: x mandatory;
        `,
        card: css<StyleProps>`
            scroll-snap-align: start;
        `,
    },
    {
        id: 6,
        label: '06 — receding shadow',
        note: 'Each card darkens toward its trailing edge, so a cut card reads as receding behind the next one instead of simply stopping.',
        card: css<StyleProps>`
            &::after {
                content: '';
                position: absolute;
                inset: 0;
                pointer-events: none;
                background: linear-gradient(to right, transparent 55%, rgba(0, 0, 0, 0.55) 100%);
            }
        `,
    },
    {
        id: 7,
        label: '07 — angled cut',
        note: 'Every card carries the same angled edge, so wherever one gets cut it reads as a deliberate shape, not an accident.',
        card: css<StyleProps>`
            clip-path: polygon(0 0, calc(100% - 2.2em) 0, 100% 100%, 0 100%);
        `,
    },
    {
        id: 8,
        label: '08 — framed edge',
        note: 'A hard frame owns the boundary outright — the cut card terminates against a deliberate edge instead of fading.',
        rail: css<StyleProps>`
            border-left: 3px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-right: 3px solid ${({ theme }) => theme.colour.LIGHT_GREY};
        `,
    },
    {
        id: 9,
        label: '09 — arrow affordance',
        note: "Chevrons over a light scrim tell you there's more instead of leaving you to infer it from the cut alone.",
        rail: overlayEdges(css<StyleProps>`
            &::before,
            &::after {
                width: 3rem;
                display: flex;
                align-items: center;
                font-size: 1.4em;
                color: ${({ theme }) => theme.colour.OFF_WHITE};
                opacity: 0.7;
            }
            &::before {
                content: '\\2039';
                justify-content: flex-start;
                padding-left: 0.5em;
                background: linear-gradient(to right, ${({ theme }) => theme.colour.DARK_GREY} 30%, transparent);
            }
            &::after {
                content: '\\203A';
                justify-content: flex-end;
                padding-right: 0.5em;
                background: linear-gradient(to left, ${({ theme }) => theme.colour.DARK_GREY} 30%, transparent);
            }
        `),
    },
    {
        id: 10,
        label: '10 — exact-fit sizing',
        note: "Card width is computed from the rail's own size so a whole number always fits — nothing is mid-card at the edges to begin with.",
        sizeToFit: 3,
    },
    {
        id: 11,
        label: '11 — plain edge',
        note: "No treatment: the rail's own container clips the cards.",
    },
];

export const DEFAULT_EDGE_VARIANT = 1;

export const getEdgeVariant = (id?: number): EdgeVariant | undefined =>
    id === undefined ? undefined : EDGE_VARIANTS.find((variant) => variant.id === id);
